import api from './api';
import {
  UserManagementData,
  UserFilters,
  UserUpdateData,
  UserRole,
} from '../types/userManagement.types';

/**
 * Get all users with optional filters (Admin only)
 */
export const getAllUsers = async (filters?: UserFilters): Promise<UserManagementData[]> => {
  const params: Record<string, string> = {};

  if (filters?.search) params.search = filters.search;
  if (filters?.verified !== undefined) params.verified = String(filters.verified);
  if (filters?.role) params.role = filters.role;
  if (filters?.isActive !== undefined) params.isActive = String(filters.isActive);

  const response = await api.get<{ data: UserManagementData[] }>('/admin/users', { params });
  return response.data.data;
};

/**
 * Get user by ID (Admin only)
 */
export const getUserById = async (userId: string): Promise<UserManagementData> => {
  const response = await api.get<{ data: UserManagementData }>(`/admin/users/${userId}`);
  return response.data.data;
};

/**
 * Update user information (Admin only)
 */
export const updateUser = async (
  userId: string,
  data: UserUpdateData
): Promise<UserManagementData> => {
  const response = await api.patch<{ data: UserManagementData }>(
    `/admin/users/${userId}`,
    data
  );
  return response.data.data;
};

/**
 * Delete user (Admin only)
 */
export const deleteUser = async (userId: string): Promise<void> => {
  await api.delete(`/admin/users/${userId}`);
};

/**
 * Verify user email manually (Admin only)
 */
export const verifyUserEmail = async (userId: string): Promise<UserManagementData> => {
  const response = await api.post<{ data: UserManagementData }>(
    `/admin/users/${userId}/verify-email`
  );
  return response.data.data;
};

/**
 * Set user role (Admin only)
 */
export const setUserRole = async (
  userId: string,
  role: UserRole
): Promise<UserManagementData> => {
  const response = await api.patch<{ data: UserManagementData }>(
    `/admin/users/${userId}/role`,
    { role }
  );
  return response.data.data;
};

/**
 * Toggle user active status (Admin only)
 */
export const toggleUserActive = async (userId: string): Promise<UserManagementData> => {
  const response = await api.patch<{ data: UserManagementData }>(
    `/admin/users/${userId}/toggle-active`
  );
  return response.data.data;
};

/**
 * Resend verification email (Admin only)
 */
export const resendVerificationEmail = async (userId: string): Promise<void> => {
  await api.post(`/admin/users/${userId}/resend-verification`);
};

/**
 * Toggle user traffic index edit permission (Admin only)
 */
export const toggleCanEditTrafficIndex = async (userId: string): Promise<UserManagementData> => {
  const response = await api.patch<{ data: UserManagementData }>(
    `/admin/users/${userId}/toggle-traffic-index`
  );
  return response.data.data;
};

/**
 * Toggle user Hangman Game Master privilege (Admin only)
 */
export const toggleIsHangmanGameMaster = async (userId: string): Promise<UserManagementData> => {
  const response = await api.patch<{ data: UserManagementData }>(
    `/admin/users/${userId}/toggle-game-master`
  );
  return response.data.data;
};
